import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Building2, LogIn } from "lucide-react";
import { usePortalAuth, formatApiError } from "@/lib/portalAuth";
import { PORTAL } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";

export default function PortalLogin() {
  usePageTitle("Portal de cliente");
  const { login } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCargando(true);
    try {
      await login(email, password);
      navigate(location.state?.from?.pathname || "/portal/unidades", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--tc-surface)] px-5">
      <div className="w-full max-w-sm rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-8 tc-fade-up">
        <div className="mb-6 flex flex-col items-center text-center">
          <span
            data-testid={PORTAL.loginLogo}
            className="flex h-11 w-11 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent)] text-white"
          >
            <Building2 size={20} strokeWidth={2.2} />
          </span>
          <h1 className="mt-3 text-lg font-semibold text-[var(--tc-text)]">Portal de cliente</h1>
          <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">Ingresá para ver el estado de tus unidades</p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Email</label>
            <input
              data-testid={PORTAL.loginEmailInput}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3.5 py-2.5 text-sm outline-none focus:border-[var(--tc-accent)] focus:ring-2 focus:ring-[var(--tc-accent-soft)]"
              placeholder="tu@empresa.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Contraseña</label>
            <input
              data-testid={PORTAL.loginPasswordInput}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3.5 py-2.5 text-sm outline-none focus:border-[var(--tc-accent)] focus:ring-2 focus:ring-[var(--tc-accent-soft)]"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p data-testid={PORTAL.loginError} className="rounded-[var(--tc-radius)] bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            data-testid={PORTAL.loginSubmitButton}
            type="submit"
            disabled={cargando}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {cargando ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
            Iniciar sesión
          </button>
        </form>

        <div className="mt-5 text-center">
          <Link data-testid={PORTAL.loginForgotPasswordLink} to="/recuperar-contrasena" className="text-sm text-[var(--tc-accent)] hover:underline">
            ¿Olvidaste tu contraseña?
          </Link>
        </div>
      </div>
    </div>
  );
}
